import { type AccountErrorCode, accountErrorTexts } from "@nexxt/account-ui";
import type { ErrorTexts } from "@nexxt/app-kit";
import type { BookingErrorCode } from "@nexxt/contracts/booking";
import type { ClaimsErrorCode } from "@nexxt/contracts/claims";
import type { FilesErrorCode } from "@nexxt/contracts/files";
import type { PartyErrorCode } from "@nexxt/contracts/party";
import type { SalesErrorCode } from "@nexxt/contracts/sales";
import { m } from "../paraglide/messages.js";

/**
 * The codes of party the screens of the customer's own administrator can meet (ExternalPartyService, ADR-172,
 * ADR-217), and those the public e-TAF of a customer can meet (PublicEtafService, ADR-175, ADR-218); an email taken or
 * locked the user form says itself (@nexxt/party-ui).
 */
type FrontrowPartyErrorCode = Extract<
  PartyErrorCode,
  | "user_invalid"
  | "user_roles_invalid"
  | "own_access"
  | "user_administered_by_tomorrow"
  | "contact_invalid"
  | "contact_not_erasable"
  | "contact_erasure_in_progress"
  | "etaf_link_invalid"
  | "etaf_not_ready_for_submission"
>;

/** The codes of sales the customer meets: the answer to the confirmation of an order (ADR-208, ADR-221). */
type FrontrowSalesErrorCode = Extract<
  SalesErrorCode,
  "order_confirmation_not_decidable" | "order_confirmation_already_decided"
>;

/**
 * The codes of claims the customer meets when it opens a claim and reads its photos (ADR-225, ADR-228); the resend of
 * a ticket and the replacement order are Tomorrow's.
 */
type FrontrowClaimsErrorCode = Extract<ClaimsErrorCode, "claim_invalid" | "claim_id_taken" | "storage_unavailable">;

/**
 * The codes of booking the customer meets on the page of the booking, from its link or from Frontrow (ADR-237); the
 * link and the contact of an email are Tomorrow's.
 */
type FrontrowBookingErrorCode = Extract<
  BookingErrorCode,
  "booking_link_invalid" | "appointment_not_bookable" | "sales_campaign_archived"
>;

// The words of the codes gateway-frontrow can answer with (ADR-046), registered at start (main.tsx): those of the
// sign-in and of the account, @nexxt/account-ui's (ADR-207), those of the users and the directory of the customer, of
// the e-TAF, of the answer to a confirmation (ADR-221), of a claim with the upload of its photos (ADR-228) and of the
// booking (ADR-237); the
// common ones are app-kit's (the documents and the shipments answer only those, ADR-220). A code without a text does
// not compile.
export const errorTexts: ErrorTexts<
  | AccountErrorCode
  | FrontrowPartyErrorCode
  | FrontrowSalesErrorCode
  | FrontrowClaimsErrorCode
  | FrontrowBookingErrorCode
  | FilesErrorCode
> = {
  ...accountErrorTexts,
  user_invalid: m.error_user_invalid,
  user_roles_invalid: m.error_user_roles_invalid,
  own_access: m.error_own_access,
  user_administered_by_tomorrow: m.error_user_administered_by_tomorrow,
  contact_invalid: m.error_contact_invalid,
  contact_not_erasable: m.error_contact_not_erasable,
  contact_erasure_in_progress: m.error_contact_erasure_in_progress,
  etaf_link_invalid: m.error_etaf_link_invalid,
  etaf_not_ready_for_submission: m.error_etaf_not_ready_for_submission,
  order_confirmation_not_decidable: m.error_order_confirmation_not_decidable,
  order_confirmation_already_decided: m.error_order_confirmation_already_decided,
  claim_invalid: m.error_claim_invalid,
  claim_id_taken: m.error_claim_id_taken,
  file_type_not_allowed: m.error_file_type_not_allowed,
  file_too_large: m.error_file_too_large,
  file_content_mismatch: m.error_file_content_mismatch,
  upload_incomplete: m.error_upload_incomplete,
  storage_unavailable: m.error_storage_unavailable,
  booking_link_invalid: m.error_booking_link_invalid,
  appointment_not_bookable: m.error_appointment_not_bookable,
  sales_campaign_archived: m.error_sales_campaign_archived,
};
